import {WS} from './utils/ws';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {RPCService} from './service/rpc-service';
import {ClientTask} from './service/client-task';
import {RPCContext, RPCContextType} from './hooks/rpc';
import {useHistory, useLocation} from 'react-router-dom';
import {App} from './components/App';
import {fetchAndInitialize} from './utils/initialize';
import {useDispatch} from 'react-redux';
import {getSession} from './utils/session-storage';
import {setApplicationLoaded} from './store/ui/actions';
import {setUserAction} from './store/user/action';

let timer;
export const RPCApplication = memo(function RPCApplication({ws}: { ws: WS }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const {pathname} = useLocation();

    const rpcRef = useRef<RPCService>();
    const [rpcService, setRPCService] = useState<RPCService>(null);
    const [connected, setConnected] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const onConnectionChanged = useCallback((isConnected: boolean, retryCount: number) => {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }

        if (isConnected) {
            timer = setInterval(() => {
                if (rpcRef.current) {
                    rpcRef.current.ping();
                }
            }, 30000);
        }

        if (isConnected) {
            setConnected(true);
        } else {
            setConnected(false);
        }
        setRetryCount(retryCount);
    }, []);

    const onOpen = useCallback(() => {
        const client = new ClientTask(dispatch);
        const rpc_service = new RPCService(client, ws);
        setRPCService(rpc_service);
        const timeout = setTimeout(() => {
            if ('127.0.0.1' === window.location.hostname) {
                rpc_service.authenticateTimeout();
            }
        }, 5000);
        getSession()
            .then(([username, password]) => {
                    return rpc_service.authenticate(username, password)
                        .then(() => {
                            history.push(pathname);
                            // noinspection JSIgnoredPromiseFromCall
                            fetchAndInitialize(rpc_service, dispatch);
                            dispatch(setUserAction(username, password));
                            clearTimeout(timeout);
                        });
                },
            ).catch(() => {
            sessionStorage.clear();
            dispatch(setApplicationLoaded());
            history.push('/login');
        });
    }, [dispatch, history, pathname, ws]);

    useEffect(() => {
        ws.dispatch = dispatch;
        ws.onConnectionChanged = onConnectionChanged;
    }, [ws, dispatch, onConnectionChanged]);

    useEffect(() => {
        ws.onopen = onOpen;
        // eslint-disable-next-line
    }, [dispatch, history, ws, onOpen]);

    useEffect(() => {
        rpcRef.current = rpcService;
    });

    return (
        <RPCContext.Provider value={[rpcService, connected, retryCount]}>
            <App/>
        </RPCContext.Provider>
    );
});
